/* ===========================================================
   API client mock — mesma forma do futuro client HTTP real:
   o tenant vive na closure (como viverá o token de auth) e
   toda resposta é assíncrona, clonada e escopada ao tenant.
   Latência simulável via VITE_MOCK_LATENCY (.env).
   =========================================================== */
import { mascararTelefoneBr } from "../domain/telefone";
import { DEMO_TENANT, getStoreData } from "../mocks/tenants";
import { LANDING_VITRINE } from "../mocks/landing-vitrine";
import type {
  ApiResult,
  AuthClient,
  AssinaturaResponse,
  CatalogoResponse,
  ContaData,
  CurvaABCData,
  DashboardData,
  DiagnosticoData,
  DoisFatoresEstado,
  LandingData,
  LucroData,
  MarketingData,
  PedidosData,
  PlanoResponse,
  RecompraData,
  ResultadoLogin,
  ResultadoVerificacao,
  SignupInput,
  StoreData,
  TenantRef,
  VerificacaoDesafio,
} from "./types";

const MOCK_LATENCY = Number(import.meta.env.VITE_MOCK_LATENCY ?? 0);

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** O tenant pode ser fixo ou resolvido por chamada (impersonação — ADR 16). */
export type TenantSource = TenantRef | (() => TenantRef);

export function createApiClient({ tenant }: { tenant: TenantSource }) {
  const resolveTenant = (): TenantRef => (typeof tenant === "function" ? tenant() : tenant);

  async function respond<T>(pick: (store: StoreData) => T): Promise<ApiResult<T>> {
    if (MOCK_LATENCY > 0) await delay(MOCK_LATENCY);
    const t = resolveTenant();
    return {
      data: structuredClone(pick(getStoreData(t.storeId))),
      tenant: t,
      generatedAt: new Date().toISOString(),
    };
  }

  // O escopo do tenant viaja anexado a cada fetcher: useApiQuery o incorpora
  // à chave do cache, para dados de uma loja nunca responderem por outra. É
  // getter (não string fixa) porque o tenant ativo pode mudar em runtime
  // (impersonação) — a cacheKey re-escopa sozinha e dispara refetch.
  const scoped = <T>(fetch: () => Promise<ApiResult<T>>) => {
    Object.defineProperty(fetch, "scope", {
      get: () => {
        const t = resolveTenant();
        return `${t.accountId}/${t.storeId}`;
      },
      enumerable: true,
      configurable: true,
    });
    return fetch as (() => Promise<ApiResult<T>>) & { scope: string };
  };

  return {
    get tenant() {
      return resolveTenant();
    },
    getDashboard: scoped<DashboardData>(() =>
      respond((s) => ({
        nuvemshop: s.nuvemshop,
        derivados: s.derivados,
        lucroSerie: s.lucroSerie,
      })),
    ),
    getDiagnostico: scoped<DiagnosticoData>(() => respond((s) => ({ derivados: s.derivados }))),
    getLucro: scoped<LucroData>(() =>
      respond((s) => ({
        nuvemshop: s.nuvemshop,
        analytics: s.analytics,
        ads: s.ads,
        derivados: s.derivados,
        waterfall: s.waterfall,
        rankings: s.rankings,
        auditoria: s.auditoria,
      })),
    ),
    getCurvaABC: scoped<CurvaABCData>(() =>
      respond((s) => ({ nuvemshop: s.nuvemshop, abcBase: s.abcBase })),
    ),
    getPedidos: scoped<PedidosData>(() =>
      respond((s) => ({ nuvemshop: s.nuvemshop, auditoria: s.auditoria })),
    ),
    getMarketing: scoped<MarketingData>(() =>
      respond((s) => ({
        nuvemshop: s.nuvemshop,
        ads: s.ads,
        marketing: s.marketing,
        auditoria: s.auditoria,
      })),
    ),
    getRecompra: scoped<RecompraData>(() =>
      respond((s) => ({ coortes: s.coortes, clientes: s.clientes })),
    ),
    getCatalogo: scoped<CatalogoResponse>(() =>
      respond((s) => ({ catalogo: s.catalogo, custos: s.custos })),
    ),
    getConta: scoped<ContaData>(() =>
      respond((s) => ({ nuvemshop: s.nuvemshop, derivados: s.derivados })),
    ),
    getAssinatura: scoped<AssinaturaResponse>(() =>
      respond((s) => ({
        assinatura: s.assinatura,
        plano: s.plano,
        faixaDetectada: s.faixaDetectada,
      })),
    ),
    getPlano: scoped<PlanoResponse>(() =>
      respond((s) => ({ plano: s.plano, faixaDetectada: s.faixaDetectada })),
    ),
    // A landing PÚBLICA mostra uma loja-VITRINE de porte grande (≈ R$ 300k/mês),
    // não a loja-demo do app — decisão de marketing (src/mocks/landing-vitrine.ts).
    // Fixa, não escopada a tenant: a landing é pública e não impersona; o pick
    // ignora a store ativa de propósito.
    getLanding: scoped<LandingData>(() => respond(() => LANDING_VITRINE)),
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;

/* ===========================================================
   Tenant ativo da sessão. O singleton `api` o resolve a cada
   chamada, então trocar aqui (impersonação do operador) re-escopa
   todas as queries sem recriar o client nem mexer nas telas. Em
   produção: o tenant vem do token de sessão; impersonar = assumir
   um token escopado ao lojista, com auditoria (ver ADR 16).
   =========================================================== */
let activeTenant: TenantRef = DEMO_TENANT;

/** Troca o tenant ativo do app (impersonação). Chamado pelo SessionProvider. */
export function setActiveTenant(t: TenantRef) {
  activeTenant = t;
}

export function getActiveTenant(): TenantRef {
  return activeTenant;
}

/** Client da sessão demo — resolve o tenant ativo a cada chamada. */
export const api = createApiClient({ tenant: () => activeTenant });

/* ===========================================================
   Auth client (e-mail+senha + OTP de WhatsApp no cadastro e 2FA).
   Não escopado a tenant — roda antes de a conta existir. Mutações
   devolvem o resultado tipado direto (sem o envelope ApiResult, que
   carrega tenant que ainda não há). O estado das verificações vive
   neste módulo, simulando o lado servidor: geração, expiração,
   cooldown de reenvio e contagem de tentativas. No mock QUALQUER
   e-mail+senha entram na loja demo — simplificação documentada em
   database-schema §6 (a validação real é do apps/api).
   Ver decisions.md §15 e §30.
   =========================================================== */
const COOLDOWN_REENVIO_S = 30;
const VALIDADE_CODIGO_MIN = 10;
const MAX_TENTATIVAS = 5;
/** Telefone do owner demo — mesmo do seed do apps/api (Camila Souza). */
const TELEFONE_DEMO = "+5511999990000";

/** Estado do 2FA da demo — persiste em localStorage para o toggle sobreviver
 *  ao reload (atalho de protótipo: inventário F10 em docs/producao-v1.md). */
const LS_2FA_DEMO = "lucraa_2fa_demo_v1";
function ler2faDemo(): boolean {
  try {
    return localStorage.getItem(LS_2FA_DEMO) === "1";
  } catch {
    return false; // ambiente sem localStorage (teste/node) — 2FA desligado
  }
}
function gravar2faDemo(ativo: boolean): void {
  try {
    if (ativo) localStorage.setItem(LS_2FA_DEMO, "1");
    else localStorage.removeItem(LS_2FA_DEMO);
  } catch {
    /* sem localStorage — estado só na sessão corrente */
  }
}

type PropositoVerificacao = "signup" | "login_2fa" | "2fa_enable";

interface VerificacaoInterna {
  codigo: string;
  telefone: string; // E.164
  expiraEmMs: number;
  reenvioLiberadoEmMs: number;
  tentativasRestantes: number;
  /** Espelho do purpose do apps/api — decide o efeito do confirmar. */
  proposito: PropositoVerificacao;
  /** null fora do signup (conta já existe — não há cadastro pendente). */
  signup: SignupInput | null;
}

let seqVerificacao = 0;
const verificacoesPendentes = new Map<string, VerificacaoInterna>();

function gerarCodigoOtp(): string {
  return String(Math.floor(100000 + Math.random() * 900000));
}

function montarDesafio(id: string, v: VerificacaoInterna): VerificacaoDesafio {
  return {
    verificacaoId: id,
    canal: "whatsapp",
    telefoneMascarado: mascararTelefoneBr(v.telefone),
    expiraEm: new Date(v.expiraEmMs).toISOString(),
    reenvioLiberadoEm: new Date(v.reenvioLiberadoEmMs).toISOString(),
    tentativasRestantes: v.tentativasRestantes,
    demoCodigo: v.codigo, // DEMO ONLY — produção entrega por WhatsApp (database-schema §6)
  };
}

export function createAuthClient(): AuthClient {
  async function comLatencia<T>(value: T): Promise<T> {
    if (MOCK_LATENCY > 0) await delay(MOCK_LATENCY);
    return value;
  }

  /** Abre um desafio OTP pendente (signup, 2FA do login ou ativação do 2FA). */
  function abrirDesafio(
    telefone: string,
    proposito: PropositoVerificacao,
    signup: SignupInput | null,
  ): VerificacaoDesafio {
    const id = `ver_${++seqVerificacao}`;
    const agora = Date.now();
    const v: VerificacaoInterna = {
      codigo: gerarCodigoOtp(),
      telefone,
      expiraEmMs: agora + VALIDADE_CODIGO_MIN * 60_000,
      reenvioLiberadoEmMs: agora + COOLDOWN_REENVIO_S * 1_000,
      tentativasRestantes: MAX_TENTATIVAS,
      proposito,
      signup,
    };
    verificacoesPendentes.set(id, v);
    return montarDesafio(id, v);
  }

  /** Cria o cadastro pendente e dispara o código que VERIFICA o telefone.
   *  No backend real a senha vira hash scrypt aqui (§30); o mock só guarda
   *  o pendente em memória. */
  async function signUp(input: SignupInput): Promise<VerificacaoDesafio> {
    return comLatencia(abrirDesafio(input.telefone, "signup", input));
  }

  /** Login e-mail+senha (§30). No mock QUALQUER credencial bem-formada entra
   *  na conta demo (database-schema §6); com o 2FA da demo ligado, devolve o
   *  desafio — a sessão só "abre" no confirmarCodigo, como no apps/api. */
  async function login(email: string, senha: string): Promise<ResultadoLogin> {
    if (!/.+@.+\..+/.test(email.trim()) || senha.length === 0) {
      throw new Error("Informe e-mail e senha para entrar.");
    }
    if (ler2faDemo()) {
      return comLatencia<ResultadoLogin>({
        status: "desafio_2fa",
        desafio: abrirDesafio(TELEFONE_DEMO, "login_2fa", null),
      });
    }
    return comLatencia<ResultadoLogin>({ status: "sessao", conta: DEMO_TENANT });
  }

  /** Reenvia um novo código (renova validade, cooldown e tentativas). */
  async function reenviarCodigo(verificacaoId: string): Promise<VerificacaoDesafio | null> {
    const v = verificacoesPendentes.get(verificacaoId);
    if (!v) return comLatencia(null);
    const agora = Date.now();
    v.codigo = gerarCodigoOtp();
    v.expiraEmMs = agora + VALIDADE_CODIGO_MIN * 60_000;
    v.reenvioLiberadoEmMs = agora + COOLDOWN_REENVIO_S * 1_000;
    v.tentativasRestantes = MAX_TENTATIVAS;
    return comLatencia(montarDesafio(verificacaoId, v));
  }

  /** Confirma o código. Código errado/expirado é resultado tipado, não exceção. */
  async function confirmarCodigo(
    verificacaoId: string,
    codigo: string,
  ): Promise<ResultadoVerificacao> {
    const v = verificacoesPendentes.get(verificacaoId);
    if (!v || Date.now() > v.expiraEmMs) {
      verificacoesPendentes.delete(verificacaoId);
      return comLatencia({ status: "expirado" });
    }
    if (codigo !== v.codigo) {
      v.tentativasRestantes -= 1;
      if (v.tentativasRestantes <= 0) {
        verificacoesPendentes.delete(verificacaoId);
        return comLatencia({ status: "expirado" });
      }
      return comLatencia({
        status: "codigo_invalido",
        tentativasRestantes: v.tentativasRestantes,
      });
    }
    verificacoesPendentes.delete(verificacaoId);
    // Sucesso → o backend cria Tenant + User(owner) (signup), abre a sessão
    // (login_2fa) ou liga o 2FA (2fa_enable). No mock a sessão segue na
    // loja-demo (único StoreData) — simplificação de database-schema §6.
    if (v.proposito === "2fa_enable") gravar2faDemo(true);
    const conta: TenantRef = v.signup
      ? { accountId: `acc_${verificacaoId}`, storeId: DEMO_TENANT.storeId }
      : DEMO_TENANT;
    return comLatencia({ status: "verificado", conta });
  }

  /* ---- 2FA da conta demo (§30) — tela /app/conta ---- */
  async function obterDoisFatores(): Promise<DoisFatoresEstado> {
    return comLatencia({
      ativo: ler2faDemo(),
      telefoneMascarado: mascararTelefoneBr(TELEFONE_DEMO),
    });
  }

  async function iniciarAtivacao2fa(): Promise<VerificacaoDesafio> {
    return comLatencia(abrirDesafio(TELEFONE_DEMO, "2fa_enable", null));
  }

  /** No backend a senha é validada de verdade; o mock só exige que exista
   *  (qualquer senha desativa — simplificação demo, database-schema §6). */
  async function desativar2fa(senha: string): Promise<void> {
    if (senha.length === 0) throw new Error("Informe sua senha para desativar.");
    gravar2faDemo(false);
    return comLatencia(undefined);
  }

  return {
    signUp,
    login,
    reenviarCodigo,
    confirmarCodigo,
    obterDoisFatores,
    iniciarAtivacao2fa,
    desativar2fa,
  };
}

/* ===========================================================
   Auth com OTP pelo serviço WhatsApp REAL (apps/whatsapp, §26 / fatia da F1).
   O frontend manda SÓ o telefone; o serviço gera, envia e valida o código
   (o código nunca volta ao browser — exceto demoCodigo em dry-run). Se o
   serviço estiver fora, cai no mock acima: a demo offline continua de pé.
   Em teste (vitest) usa só o mock — determinístico, sem rede.
   =========================================================== */
interface DadosPendentes {
  telefone: string;
  signup: SignupInput;
}

interface OtpRequestResp {
  id: string;
  expiraEm: string;
  reenvioLiberadoEm: string;
  demoCodigo?: string;
}

export function createAuthClientServico(): AuthClient {
  const mock = createAuthClient();
  const usarServico = import.meta.env.MODE !== "test";
  const pendentes = new Map<string, DadosPendentes>(); // id do serviço → dados do cadastro

  async function pedirOtp(telefone: string): Promise<OtpRequestResp> {
    const r = await fetch("/wa-public/otp/request", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ to: telefone, purpose: "signup" }),
    });
    if (!r.ok) throw new Error(`otp_request ${r.status}`);
    return (await r.json()) as OtpRequestResp;
  }

  function desafioDe(id: string, telefone: string, j: OtpRequestResp): VerificacaoDesafio {
    return {
      verificacaoId: id,
      canal: "whatsapp",
      telefoneMascarado: mascararTelefoneBr(telefone),
      expiraEm: j.expiraEm,
      reenvioLiberadoEm: j.reenvioLiberadoEm,
      tentativasRestantes: MAX_TENTATIVAS,
      demoCodigo: j.demoCodigo,
    };
  }

  async function signUp(input: SignupInput): Promise<VerificacaoDesafio> {
    if (!usarServico) return mock.signUp(input);
    try {
      const j = await pedirOtp(input.telefone);
      pendentes.set(j.id, { telefone: input.telefone, signup: input });
      return desafioDe(j.id, input.telefone, j);
    } catch {
      return mock.signUp(input); // serviço fora → fallback offline
    }
  }

  /** Login DELEGA ao mock: o serviço WhatsApp só entrega códigos — quem valida
   *  e-mail+senha é o apps/api, então login real é exclusivo do modo http
   *  (createAuthClientHttp). Aqui a demo segue offline. */
  async function login(email: string, senha: string): Promise<ResultadoLogin> {
    return mock.login(email, senha);
  }

  async function reenviarCodigo(verificacaoId: string): Promise<VerificacaoDesafio | null> {
    const p = pendentes.get(verificacaoId);
    if (!p) return mock.reenviarCodigo(verificacaoId);
    try {
      const j = await pedirOtp(p.telefone);
      pendentes.delete(verificacaoId);
      pendentes.set(j.id, p);
      return desafioDe(j.id, p.telefone, j);
    } catch {
      return null;
    }
  }

  async function confirmarCodigo(
    verificacaoId: string,
    codigo: string,
  ): Promise<ResultadoVerificacao> {
    const p = pendentes.get(verificacaoId);
    if (!p) return mock.confirmarCodigo(verificacaoId, codigo);
    try {
      const r = await fetch("/wa-public/otp/verify", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ id: verificacaoId, code: codigo }),
      });
      const j = (await r.json()) as { status: string; tentativasRestantes?: number };
      if (j.status === "verified") {
        pendentes.delete(verificacaoId);
        return {
          status: "verificado",
          conta: { accountId: `acc_${verificacaoId}`, storeId: DEMO_TENANT.storeId },
        };
      }
      if (j.status === "codigo_invalido") {
        return { status: "codigo_invalido", tentativasRestantes: j.tentativasRestantes ?? 0 };
      }
      pendentes.delete(verificacaoId); // expirado | tentativas_excedidas | nao_encontrado
      return { status: "expirado" };
    } catch {
      return { status: "expirado" };
    }
  }

  return {
    signUp,
    login,
    reenviarCodigo,
    confirmarCodigo,
    // 2FA é estado da conta demo — sempre no mock (o serviço só entrega códigos)
    obterDoisFatores: () => mock.obterDoisFatores(),
    iniciarAtivacao2fa: () => mock.iniciarAtivacao2fa(),
    desativar2fa: (senha: string) => mock.desativar2fa(senha),
  };
}

/** Site público (ADR §38): auth é 100% MOCK — não há backend nem serviço WhatsApp.
 *  `createAuthClientServico` (com fetch a /wa-public) fica sem uso e é tree-shaken
 *  do bundle; o 2FA da conta segue funcionando pelo mock. */
export const authApi: AuthClient = createAuthClient();
